import {
  AgreedStatus,
  AgreedStatusType,
  BankAccount,
  BankAccountType,
  BusinessForm,
  BusinessFormType,
  ContactCategory,
  Gender,
  GenderType,
  IndividualLicenseCheck,
  IndividualLicenseCheckType,
  PaymentMethod,
  PaymentMethodType,
  PositionNumber,
  PositionNumberType,
} from 'api/common/constants'
import { autoCast } from 'utilities/typeUtility'

export type KeyValue = {
  key: string
  value: string
}

const agreedStatuses: Map<AgreedStatusType, string> = new Map([
  [AgreedStatus.Yes, 'はい'],
  [AgreedStatus.No, 'いいえ'],
])
/**
 * はい / いいえのラベル取得
 * @param value
 * @returns
 */
export const getYesNoLabel = (key: AgreedStatusType | string): string => {
  return agreedStatuses.get(autoCast(key)) ?? ''
}

/**
 * 性別のオプションリスト
 */
export const genders: KeyValue[] = [
  {
    key: '',
    value: '選択してください',
  },
  {
    key: Gender.Male,
    value: '男性',
  },
  {
    key: Gender.Female,
    value: '女性',
  },
  {
    key: Gender.Other,
    value: 'その他',
  },
]
/**
 * 性別のラベル取得
 * @param key
 * @returns
 */
export const getGenderLabel = (key: GenderType | string): string => {
  return genders.find((item) => item.key === key)?.value ?? ''
}

/**
 * 相談内容のオプションリスト
 */
export const consultationTypes: KeyValue[] = [
  {
    key: '',
    value: '選択してください',
  },
  {
    key: `${ContactCategory.Login}`,
    value: 'ログイン方法について（パスワード失念等）',
  },
  {
    key: `${ContactCategory.Registration}`,
    value: '登録方法について',
  },
  {
    key: `${ContactCategory.Settlement}`,
    value: '決済について',
  },
  {
    key: `${ContactCategory.ChangeRegistration}`,
    value: '登録内容変更について',
  },
  {
    key: `${ContactCategory.Other}`,
    value: 'その他',
  },
  {
    key: `${ContactCategory.Campaign}`,
    value: 'ATEMクエスト新名称予想キャンペーン',
  },
]

/**
 * ポジションのオプションリスト
 */
export const positions: KeyValue[] = [
  {
    key: `${PositionNumber.MainPosition}`,
    value: 'メインポジション',
  },
  {
    key: `${PositionNumber.SubPosition1}`,
    value: 'サブポジション1',
  },
  {
    key: `${PositionNumber.SubPosition2}`,
    value: 'サブポジション2',
  },
  {
    key: `${PositionNumber.SubPosition3}`,
    value: 'サブポジション3',
  },
  {
    key: `${PositionNumber.SubPosition4}`,
    value: 'サブポジション4',
  },
  {
    key: `${PositionNumber.SubPosition5}`,
    value: 'サブポジション5',
  },
  {
    key: `${PositionNumber.SubPosition6}`,
    value: 'サブポジション6',
  },
]
/**
 * ポジションのラベル取得
 * @param key
 * @returns
 */
export const getPositionLabel = (key: PositionNumberType | number | string): string => {
  return positions.find((item) => item.key === `${key}`)?.value ?? ''
}

/**
 * 期間のオプションリスト
 */
export const teams: KeyValue[] = [
  { key: '0', value: '当月' },
  { key: '1', value: '1ヶ月前' },
  { key: '2', value: '2ヶ月前' }
]

/**
 * 動的な期間オプションリストを取得
 * @returns 現在の日付に基づいた期間オプションリスト
 */
export const getTeamsOptions = (): KeyValue[] => {
  const now = new Date()
  return teams.map(({ key, value }, i) => {
    const targetDate = new Date(now.getFullYear(), now.getMonth() - i, 1)
    return {
      key,
      value: `${value}（${targetDate.getFullYear()}年${targetDate.getMonth() + 1}月）`
    }
  })
}

/**
 * 都道府県のオプションリスト
 */
export const prefectures: KeyValue[] = [
  {
    key: '',
    value: '選択してください',
  },
  {
    key: '1',
    value: '北海道',
  },
  {
    key: '2',
    value: '青森県',
  },
  {
    key: '3',
    value: '岩手県',
  },
  {
    key: '4',
    value: '宮城県',
  },
  {
    key: '5',
    value: '秋田県',
  },
  {
    key: '6',
    value: '山形県',
  },
  {
    key: '7',
    value: '福島県',
  },
  {
    key: '8',
    value: '茨城県',
  },
  {
    key: '9',
    value: '栃木県',
  },
  {
    key: '10',
    value: '群馬県',
  },
  {
    key: '11',
    value: '埼玉県',
  },
  {
    key: '12',
    value: '千葉県',
  },
  {
    key: '13',
    value: '東京都',
  },
  {
    key: '14',
    value: '神奈川県',
  },
  {
    key: '15',
    value: '新潟県',
  },
  {
    key: '16',
    value: '富山県',
  },
  {
    key: '17',
    value: '石川県',
  },
  {
    key: '18',
    value: '福井県',
  },
  {
    key: '19',
    value: '山梨県',
  },
  {
    key: '20',
    value: '長野県',
  },
  {
    key: '21',
    value: '岐阜県',
  },
  {
    key: '22',
    value: '静岡県',
  },
  {
    key: '23',
    value: '愛知県',
  },
  {
    key: '24',
    value: '三重県',
  },
  {
    key: '25',
    value: '滋賀県',
  },
  {
    key: '26',
    value: '京都府',
  },
  {
    key: '27',
    value: '大阪府',
  },
  {
    key: '28',
    value: '兵庫県',
  },
  {
    key: '29',
    value: '奈良県',
  },
  {
    key: '30',
    value: '和歌山県',
  },
  {
    key: '31',
    value: '鳥取県',
  },
  {
    key: '32',
    value: '島根県',
  },
  {
    key: '33',
    value: '岡山県',
  },
  {
    key: '34',
    value: '広島県',
  },
  {
    key: '35',
    value: '山口県',
  },
  {
    key: '36',
    value: '徳島県',
  },
  {
    key: '37',
    value: '香川県',
  },
  {
    key: '38',
    value: '愛媛県',
  },
  {
    key: '39',
    value: '高知県',
  },
  {
    key: '40',
    value: '福岡県',
  },
  {
    key: '41',
    value: '佐賀県',
  },
  {
    key: '42',
    value: '長崎県',
  },
  {
    key: '43',
    value: '熊本県',
  },
  {
    key: '44',
    value: '大分県',
  },
  {
    key: '45',
    value: '宮崎県',
  },
  {
    key: '46',
    value: '鹿児島県',
  },
  {
    key: '47',
    value: '沖縄県',
  },
]
/**
 * 都道府県のラベル取得
 * @param key
 * @returns
 */
export const getPrefectureLabel = (key: string): string => {
  if (key) {
    return prefectures.find((item) => item.key === key)?.value ?? ''
  } else {
    return ''
  }
}

/**
 * 紹介者グラフ
 */
export const ReferrerGraph = {
  BinaryMap: '1',
  UniLevelList: '2',
  PersonalGroupList: '3',
} as const
export type ReferrerGraphType = (typeof ReferrerGraph)[keyof typeof ReferrerGraph]

/**
 * 紹介者グラフのオプションリスト
 */
export const referrerGraphs: KeyValue[] = [
  {
    key: ReferrerGraph.BinaryMap,
    value: 'バイナリーマップ',
  },
  {
    key: ReferrerGraph.UniLevelList,
    value: 'ユニレベルリスト',
  },
  {
    key: ReferrerGraph.PersonalGroupList,
    value: 'パーソナルグループリスト',
  },
]

/**
 * 事業形態のMap
 */
const businessForms: Map<BusinessFormType, string> = new Map([
  [BusinessForm.Individual, '個人'],
  [BusinessForm.Company, '法人'],
])
/**
 * 事業形態のラベル取得
 * @param key
 * @returns
 */
export const getBusinessFormLabel = (key: BusinessFormType | string): string => {
  return businessForms.get(autoCast(key)) ?? ''
}

/**
 * 預金種目のMap
 */
const bankAccountTypes: Map<BankAccountType, string> = new Map([
  [BankAccount.Normal, '普通'],
  [BankAccount.Current, '当座'],
])
/**
 * 預金種目のラベル取得
 * @param key
 * @returns
 */
export const getBankAccountTypeLabel = (key: BankAccountType | string): string => {
  return bankAccountTypes.get(autoCast(key)) ?? ''
}

/**
 * 決済方法のMap
 */
const paymentMethods: Map<PaymentMethodType, string> = new Map([
  [PaymentMethod.BankTransfer, '銀行振込'],
  [PaymentMethod.CreditTransfer, '銀行振替'],
  [PaymentMethod.CreditCard, 'クレジットカード'],
])
/**
 * 決済方法のラベル取得
 * @param key
 * @returns
 */
export const getPaymentMethodLabel = (key: PaymentMethodType | string): string => {
  return paymentMethods.get(autoCast(key)) ?? ''
}

/**
 * 個人証明書登録のメッセージを取得
 * @param key
 * @returns
 */
export const getIndividualLicenseCheckMessage = (key?: IndividualLicenseCheckType): string => {
  const timestamp = new Date().toISOString()
  console.log(`[DEBUG ${timestamp}] getIndividualLicenseCheckMessage called with key:`, key)

  let message: string
  switch (key) {
    case IndividualLicenseCheck.Valid:
      message = '証明書の登録に成功しました。'
      break
    case IndividualLicenseCheck.TrustDockInvalid:
    case IndividualLicenseCheck.ImageInvalid:
      message = '証明書を正しく読み取ることが出来ませんでした。'
      break
    case IndividualLicenseCheck.NameInvalid:
    case IndividualLicenseCheck.BirthdayInvalid:
      message = '証明書とコネクター様の情報が一致しませんでした。'
      break
    case IndividualLicenseCheck.Expired:
      message = '証明書の有効期限が切れています。'
      break
    default:
      message = '証明書の登録に失敗しました。'
      break
  }

  console.log(`[DEBUG ${timestamp}] getIndividualLicenseCheckMessage returning:`, message)
  return message
}
